/**
 * SessionCopilotPanel — Slice 2 of the AI platform brief (session facilitation
 * copilot), behind the `session_copilot` module + flag gate (mounted
 * conditionally by SessionDetail; this component assumes the gate passed).
 *
 * The header line (session state + next incomplete Frame stage) is
 * DETERMINISTIC — rendered from the session packet's pure stage mapping, never
 * from the model. The LLM only answers questions and offers copyable text
 * drafts; it never acts, scores, overrides, or ranks (voice, never authority).
 */

import { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, Circle, Compass, Copy, Loader2 } from 'lucide-react';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { resolveRunMode } from '../../../types/prioritization/session';
import { buildSessionPacket, type SessionPacket } from '../../../lib/ai/contextPacket';
import { askSessionCopilot, type SessionCopilotResult } from '../../../lib/llmSessionCopilotService';
import { getStoredUiMode } from '../../../lib/uiMode';
import { useVoicesStore } from '../../../features/voices';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';

const STATE_LABELS = SESSION_STATE_LABELS;

export interface SessionCopilotPanelProps {
  session: PrioritizationSession;
  participantCount: number;
  itemCount: number;
}

export function SessionCopilotPanel({ session, participantCount, itemCount }: SessionCopilotPanelProps) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState<SessionCopilotResult | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const uiMode = getStoredUiMode('sessions');
  const isCMode = uiMode === 'c';

  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  // Read-only: seats already loaded by the Participants surface, never fetched here.
  const seats = useVoicesStore((s) => s.seatsBySession[session.id]);
  const voices = useVoicesStore((s) => s.voices);

  const packet: SessionPacket = useMemo(() => {
    const voiceSeats = seats
      ? seats
          .filter((seat) => seat.status === 'active')
          .map((seat) => ({
            name: voices.find((v) => v.id === seat.voiceId)?.displayName ?? 'Voice',
            seatType: seat.seatType,
            countingPolicy: seat.countingPolicy,
          }))
      : undefined;
    return buildSessionPacket({
      session: {
        id: session.id,
        name: session.name ?? null,
        state: session.state,
        runMode: session.modeConfig ? resolveRunMode(session.modeConfig) : null,
      },
      roster: {
        participantCount,
        facilitatorName: session.facilitatorId && session.facilitatorId === userId ? userName : null,
        voiceSeats,
      },
      criteria: {
        lockedAt: session.criteriaFinalizedAt
          ? new Date(session.criteriaFinalizedAt).toISOString()
          : null,
      },
      pool: { itemCount },
      uiMode,
    });
  }, [session, participantCount, itemCount, seats, voices, userId, userName, uiMode]);

  // Deterministic ball/stage line — from the packet, never the model.
  const stateLabel = STATE_LABELS[session.state];
  const nextStageLabel = packet.frame.incompleteStageLabels[0] ?? null;
  const headerLine = isCMode
    ? nextStageLabel
      ? `This session is at "${stateLabel}". Next up: ${nextStageLabel.toLowerCase()}.`
      : `This session is at "${stateLabel}". All six stages are complete.`
    : nextStageLabel
      ? `State: ${stateLabel} — next incomplete stage: ${nextStageLabel} (${packet.frame.incompleteStageLabels.length} of 6 remaining).`
      : `State: ${stateLabel} — all six Frame stages complete.`;

  const ask = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(true);
    setResult(null);
    setUnavailable(false);
    try {
      const res = await askSessionCopilot({ packet, question: q, uiMode });
      if (res) setResult(res);
      else setUnavailable(true);
    } finally {
      setAsking(false);
    }
  };

  const copyDraft = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      window.setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 1500);
    } catch {
      // Clipboard unavailable — leave the text selectable in the card.
    }
  };

  const drafts = result ? (isCMode ? result.suggestedDrafts.slice(0, 1) : result.suggestedDrafts) : [];

  return (
    <div className="border border-[var(--green)] bg-[var(--green-dim)]/50 dark:border-[var(--green)]/50 dark:bg-[var(--green)]/20">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
        )}
        <Compass className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Session copilot
        </span>
        <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate">
          — {headerLine}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* Deterministic stage strip — rendered from the packet, not the LLM */}
          {!isCMode && (
            <div className="flex items-center gap-3 flex-wrap text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              {packet.frame.stages.map((s) => (
                <span key={s.stage} className="inline-flex items-center gap-1">
                  {s.complete ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] dark:text-[var(--text-ter)]" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
                  )}
                  {s.label}
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void ask();
              }}
              placeholder={
                isCMode
                  ? 'Ask about this session — e.g. "What happens next?"'
                  : 'Ask about this session — e.g. "Why can\'t we rank yet?"'
              }
              className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:ring-2 focus:ring-teal-500 focus:border-[var(--green)]"
            />
            <button
              type="button"
              onClick={() => void ask()}
              disabled={asking || !question.trim()}
              className="px-3 py-2 text-sm font-medium text-white bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ask'}
            </button>
          </div>

          {unavailable && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {isCMode
                ? 'The copilot is not available right now. The status line above is still accurate.'
                : 'The copilot is unavailable (no AI provider reachable or the assist is off). The deterministic status line above is still accurate.'}
            </p>
          )}

          {result && (
            <div className="space-y-3">
              <div className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] whitespace-pre-wrap">
                {result.reply}
              </div>
              {drafts.length > 0 && (
                <div className="space-y-2">
                  {drafts.map((d, idx) => (
                    <div
                      key={`${d.label}-${idx}`}
                      className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                          {isCMode ? 'Suggested note' : `Draft — ${d.label}`}
                        </span>
                        <button
                          type="button"
                          onClick={() => void copyDraft(d.text, idx)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          {copiedIdx === idx ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] whitespace-pre-wrap">
                        {d.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                AI-generated. Drafts are text for you to review and send — the copilot never acts on the session.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
